import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import Flag from "react-world-flags";
import { conversationSubjects, languageToFlag, supportedLanguages } from "../../utilities/languages";

export default function SpeechBubble({
	data,
	onGenerateQuestion,
	initialLanguage = "en",
	onLanguageChange,
	initialSubject = "userData",
	onSubjectChange,
}) {
	const [showMainMenu, setShowMainMenu] = useState(false);
	const [showLanguageModal, setShowLanguageModal] = useState(false);
	const [showSubjectModal, setShowSubjectModal] = useState(false);
	const [questions, setQuestions] = useState([]);
	const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
	const [selectedSubject, setSelectedSubject] = useState(initialSubject);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [loading, setLoading] = useState(false);
	const abortRef = useRef(null);

	const closeAllModals = () => {
		setShowMainMenu(false);
		setShowLanguageModal(false);
		setShowSubjectModal(false);
	};

	const backToMainMenu = () => {
		setShowLanguageModal(false);
		setShowSubjectModal(false);
		setShowMainMenu(true);
	};

	const hasFetchedRef = useRef(false);

	const fetchQuestion = useCallback(
		async ({ append = false } = {}) => {
			if (!data) return;
			if (!append && hasFetchedRef.current) return; // ⛔ block initial double fire
			if (!append) hasFetchedRef.current = true;

			if (abortRef.current) abortRef.current.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			setLoading(true);

			try {
				const q = await onGenerateQuestion({
					...data,
					language: selectedLanguage,
					subject: selectedSubject,
					signal: controller.signal,
				});

				const cleanQuestion = q.replace(/^["']|["']$/g, "");
				setQuestions((prev) => (append ? [...prev, cleanQuestion] : [cleanQuestion]));
				setCurrentIndex(append ? (prev) => prev + 1 : 0);
			} catch {
				setQuestions((prev) =>
					append ? [...prev, "Failed to generate question 😅"] : ["Failed to generate question 😅"],
				);
				setCurrentIndex(append ? (prev) => prev + 1 : 0);
			} finally {
				setLoading(false);
			}
		},
		[data, selectedLanguage, selectedSubject, onGenerateQuestion],
	);

	// 🔹 Initial load + language change
	useEffect(() => {
		hasFetchedRef.current = false;
	}, [data?.uid, selectedLanguage, selectedSubject]);

	useEffect(() => {
		fetchQuestion();
	}, [fetchQuestion]);

	// 🔹 Next button
	const handleNext = () => {
		if (currentIndex === questions.length - 1) {
			fetchQuestion({ append: true });
		} else {
			setCurrentIndex((prev) => prev + 1);
		}
	};

	// 🔹 Previous button
	const handlePrev = () => {
		if (currentIndex > 0) {
			setCurrentIndex((prev) => prev - 1);
		}
	};

	return (
		<div className="d-flex justify-content-center align-items-center">
			<Card className="p-2 shadow bubble speech-pop d-flex flex-row align-items-center justify-content-center">
				<Button variant="light" onClick={handlePrev} disabled={currentIndex === 0}>
					<i className="bi bi-chevron-left"></i>
				</Button>

				<Card.Text className="flex-grow-1 mb-0 text-center" style={{ fontSize: "1.4em" }}>
					{loading ? <TypingDots /> : questions[currentIndex]}
				</Card.Text>

				<div className="d-flex align-items-center">
					<Button variant="light" onClick={handleNext}>
						<i className="bi bi-chevron-right"></i>
					</Button>

					<Button variant="light" className="ms-2" onClick={() => setShowMainMenu(true)}>
						<i className="bi bi-gear-fill"></i>
					</Button>
				</div>
			</Card>

			{/* Main Menu Modal */}
			<Modal show={showMainMenu} onHide={closeAllModals} centered>
				<Modal.Header>
					<div className="d-flex justify-content-between align-items-center w-100">
						<div /> {/* spacer keeps title centered */}
						<Modal.Title>Settings</Modal.Title>
						<button className="btn-close" onClick={closeAllModals} />
					</div>
				</Modal.Header>

				<Modal.Body>
					{/* Active language indicator */}
					<div className="mb-3 text-center">
						<small className="text-muted">Current language</small>

						<div className="fw-bold fs-5 d-flex justify-content-center align-items-center gap-2">
							<span>{selectedLanguage}</span>

							{selectedLanguage === "Catalan" ? (
								<img
									src="/images/Flag_of_Catalonia.svg.png"
									alt="Catalan Flag"
									className="catalan-flag"
									style={{ width: "30px", height: "20px" }}
								/>
							) : (
								languageToFlag[selectedLanguage] && (
									<Flag code={languageToFlag[selectedLanguage]} style={{ width: "30px", height: "20px" }} />
								)
							)}
						</div>
						{/* Subject */}
						{selectedSubject && (
							<div className="mt-1">
								<small className="text-muted">Current subject</small>
								<div className="fw-semibold">
									{selectedSubject === "userData" ? "Data From Profile".toUpperCase() : selectedSubject.toUpperCase()}
								</div>
							</div>
						)}
					</div>

					<div className="d-flex flex-column gap-2">
						<Button
							className="my-btn"
							onClick={() => {
								setShowMainMenu(false);
								setShowLanguageModal(true);
							}}
						>
							<i className="bi bi-translate me-2"></i> Change Language
						</Button>

						<Button
							className="my-btn"
							onClick={() => {
								setShowMainMenu(false);
								setShowSubjectModal(true);
							}}
						>
							<i className="bi bi-star-fill me-2"></i> Change Subject
						</Button>
					</div>
				</Modal.Body>
			</Modal>

			{/* Language Modal */}
			<Modal show={showLanguageModal} onHide={closeAllModals} centered>
				<Modal.Header>
					<div className="d-flex justify-content-between align-items-center w-100">
						{/* LEFT — Back */}
						<button className="icon-btn mx-2" onClick={backToMainMenu}>
							<i className="bi bi-arrow-left fs-3"></i>
						</button>

						<Modal.Title style={{ fontSize: "1rem" }}>Select Question Language</Modal.Title>

						{/* RIGHT — Close */}
						<button className="btn-close" onClick={closeAllModals} />
					</div>
				</Modal.Header>

				<Modal.Body>
					<div className="d-flex flex-column gap-2">
						{supportedLanguages.map((lang) => (
							<Button
								key={lang}
								variant={selectedLanguage === lang ? "primary" : "outline-primary"}
								onClick={() => {
									if (lang === selectedLanguage) {
										setShowLanguageModal(false);
										return;
									}

									setSelectedLanguage(lang); // 🔹 updates UI + reloads question
									onLanguageChange?.(lang); // 🔹 saves to Firestore (parent)
									setShowLanguageModal(false);
								}}
							>
								{lang.toUpperCase()}
								{lang === "Catalan" ? (
									<img
										src="/images/Flag_of_Catalonia.svg.png"
										alt="Catalan Flag"
										className="catalan-flag ms-2"
										style={{ width: "20px", height: "12px" }}
									/>
								) : (
									languageToFlag[lang] && (
										<Flag
											code={languageToFlag[lang]}
											className="ms-2 flags"
											style={{ width: "20px", height: "15px" }}
										/>
									)
								)}
							</Button>
						))}
					</div>
				</Modal.Body>
			</Modal>

			{/* Subject Modal */}
			<Modal show={showSubjectModal} onHide={closeAllModals} centered>
				<Modal.Header>
					<div className="d-flex justify-content-between align-items-center w-100">
						{/* LEFT — Back */}
						<button className="icon-btn mx-2" onClick={backToMainMenu}>
							<i className="bi bi-arrow-left fs-3"></i>
						</button>

						<Modal.Title style={{ fontSize: "1rem" }}>Select Conversation Subject</Modal.Title>

						{/* RIGHT — Close */}
						<button className="btn-close" onClick={closeAllModals} />
					</div>
				</Modal.Header>

				<Modal.Body>
					<div className="d-flex flex-column gap-2">
						{conversationSubjects.map((subject) => (
							<Button
								key={subject}
								variant={selectedSubject === subject ? "primary" : "outline-primary"}
								onClick={() => {
									setSelectedSubject(subject);
									onSubjectChange?.(subject);
									setShowSubjectModal(false);
								}}
							>
								{subject === "userData" ? "Data From Profile".toUpperCase() : subject.toUpperCase()}
							</Button>
						))}
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
}

function TypingDots() {
	return (
		<span className="typing-dots" style={{ marginLeft: "5px" }}>
			<span>.</span>
			<span>.</span>
			<span>.</span>
		</span>
	);
}
